import { useState, useEffect } from 'react';
import { X, Copy, Eye, EyeOff } from 'lucide-react';
import api from '../../services/api';
import styles from './MT5Modal.module.css';

const DOWNLOADS = {
  windows: 'https://download.terminal.free/cdn/web/metaquotes.ltd/mt5/mt5setup.exe?utm_source=www.metatrader5.com&utm_campaign=download',
  mac:     'https://download.terminal.free/cdn/web/metaquotes.ltd/mt5/MetaTrader5.pkg.zip?utm_source=www.metatrader5.com&utm_campaign=download.mt5.macos',
  android: 'https://download.terminal.free/cdn/mobile/mt5/android?hl=en&utm_source=www.metatrader5.com&utm_campaign=install.metaquotes',
  apple:   'https://download.terminal.free/cdn/mobile/mt5/ios?hl=en&utm_source=www.metatrader5.com&utm_campaign=install.metaquotes',
};

function MT5Modal({ login, onClose }) {
  const [showMaster, setShowMaster] = useState(false);
  const [showInvestor, setShowInvestor] = useState(false);
  const [copied, setCopied] = useState('');
  const [creds, setCreds] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    api.get(`/assets/mt5/${login}`)
      .then(({ data }) => { if (active) setCreds(data); })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [login]);

  const copy = (key, value) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    setCopied(key);
    setTimeout(() => setCopied(''), 1500);
  };

  const handleWindowsDownload = () => { window.location.href = DOWNLOADS.windows; };
  const handleMacDownload = () => { window.location.href = DOWNLOADS.mac; };

  const dash = '-';

  return (
    <div className={styles.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>
        <div className={styles.dragHandle} />
        <div className={styles.header}>
          <span className={styles.title}>MT5 login</span>
          <button className={styles.closeBtn} onClick={onClose}><X size={20} strokeWidth={2.5} /></button>
        </div>

        {error ? (
          <div className={styles.row}><span className={styles.rowLabel}>Couldn't load login details</span></div>
        ) : (
        <>
        <div className={styles.row}>
          <span className={styles.rowLabel}>Account</span>
          <span className={styles.rowValue}>
            {loading ? dash : creds.accountNumber}
            <button className={styles.iconBtn} onClick={() => copy('account', creds?.accountNumber)}>
              <Copy size={14} />
            </button>
          </span>
        </div>

        {!creds?.hidePasswords && (
        <>
        <div className={styles.row}>
          <span className={styles.rowLabel}>Master password</span>
          <span className={styles.rowValue}>
            {loading ? dash : (showMaster ? creds.masterPassword : '••••••')}
            <button className={styles.iconBtn} onClick={() => setShowMaster(p => !p)}>
              {showMaster ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
            <button className={styles.iconBtn} onClick={() => copy('master', creds?.masterPassword)}>
              <Copy size={14} />
            </button>
          </span>
        </div>

        <div className={styles.row}>
          <span className={styles.rowLabel}>Investor password</span>
          <span className={styles.rowValue}>
            {loading ? dash : (showInvestor ? creds.investorPassword : '••••••')}
            <button className={styles.iconBtn} onClick={() => setShowInvestor(p => !p)}>
              {showInvestor ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
            <button className={styles.iconBtn} onClick={() => copy('investor', creds?.investorPassword)}>
              <Copy size={14} />
            </button>
          </span>
        </div>

        </>
        )}

        <div className={styles.row}>
          <span className={styles.rowLabel}>MT5 Server</span>
          <span className={styles.rowValue}>{loading ? dash : creds.server}</span>
        </div>
        </>
        )}

        {copied && <span className={styles.copiedHint}>Copied {copied}</span>}

        <p className={styles.downloadTitle}>Download platform</p>

        {/* Desktop: Windows + Mac */}
        <div className={styles.downloadBtns}>
          <button className={styles.downloadBtn} onClick={handleWindowsDownload}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.551H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801"/></svg>
            Windows
          </button>
          <button className={styles.downloadBtn} onClick={handleMacDownload}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/></svg>
            Mac
          </button>
        </div>

        {/* Mobile: App Store + Google Play */}
        <div className={styles.downloadBadges}>
          <a
            className={styles.badge}
            href={DOWNLOADS.apple}
            target="_blank"
            rel="noreferrer"
          >
            <img src="/ios_light.svg" className={styles.badgeImgLight} alt="Download on the App Store" />
            <img src="/ios_dark.svg"  className={styles.badgeImgDark}  alt="Download on the App Store" />
          </a>
          <a
            className={styles.badge}
            href={DOWNLOADS.android}
            target="_blank"
            rel="noreferrer"
          >
            <img src="/android_light.svg" className={styles.badgeImgLight} alt="Get it on Google Play" />
            <img src="/android_dark.svg"  className={styles.badgeImgDark}  alt="Get it on Google Play" />
          </a>
        </div>
      </div>
    </div>
  );
}

export default MT5Modal;
